import { withNamespaces } from "react-i18next"
import Accordion from 'react-bootstrap/Accordion';
import { Card, CardBody, Col, Row, Table } from "reactstrap";
import DateUtils from "src/services/utils/DateUtils";
import { buildNestedObject } from "../../helpers";
import { RiArchiveLine, RiBuilding2Line, RiHome2Line, RiMapPin2Line, RiOrganizationChart, RiUser3Line } from "react-icons/ri";
import { KeysTitles } from "../../constants";
import { INTERNATIONAL_DATE_FORMAT } from 'src/common/constants';

const Establishment = ({
    t,
    activeAccordion,
    setActiveAccordion,

    detailsQuery
}) => {
    if (detailsQuery.isFetching) {
        return (
            <Row>
                <Col 
                    sm="12" 
                    className="mt-4 mb-4"
                >
                    <div 
                        className="dt-field dt-skeleton" 
                        style={{ width: '100%', height: '100px' }}
                    />
                </Col>
            </Row>
        )
    }

    const dateUtils = new DateUtils();

    const organizationData = JSON.parse(detailsQuery.data?.company?.organizationData || "");

    const generic = buildNestedObject(organizationData?.additional_information?.generic || []);

    const establishmentDetails = generic?.establishmentDetails || {};

    const otherEstablishmentDetails = generic?.otherEstablishmentDetails || {};

    const branches = generic?.branches || [];

    const additionalAuditorInformation = generic?.additionalAuditorInformation || [];

    const misc = generic?.misc || {};

    return (
        <Accordion.Item
            onClick={() => setActiveAccordion('establishments')}
            className={activeAccordion === 'establishments' ? 'active' : ''}
            eventKey={'establishments'}
        >
            <Accordion.Header>
                <RiBuilding2Line size="24" className="me-2" />
                <span>
                    {t('Establishments & Structure')}
                </span>
            </Accordion.Header>

            <Accordion.Body>
                {/* Headquarters / Main Establishment */}
                <Card className="h-100 h-100 shadow-sm mb-4">
                    <CardBody className="p-1">
                        <div className="d-flex align-items-center mb-2">
                            <span className="me-2 text-primary">
                                <RiHome2Line size="24" />
                            </span>

                            <div className="fw-bold font-size-16">
                                {t('Headquarters / Main Establishment')}
                            </div>
                        </div>

                        <Row>
                            {Object.entries(establishmentDetails).map(([key, value]) => (
                                <Col sm="6" key={key} className="mb-1">
                                    <strong className="me-1">
                                        {t(KeysTitles[key]?.title || key)}:
                                    </strong> 
                                    {key === 'formationDate' ? dateUtils.convertDateToDate(value, INTERNATIONAL_DATE_FORMAT) : value}
                                </Col>
                            ))}
                        </Row>
                    </CardBody>
                </Card>

                {/* Other Establishment Details */}
                <Card className="h-100 h-100 shadow-sm mb-4">
                    <CardBody className="p-1">
                        <div className="d-flex align-items-center mb-2">
                            <span className="me-2 text-primary">
                                <RiMapPin2Line size="24" />
                            </span>

                            <div className="fw-bold font-size-16">
                                {t('Other Establishment Details')}
                            </div>
                        </div>

                        <Row>
                            {Object.entries(otherEstablishmentDetails).map(([key, value]) => (
                                <Col sm="6" key={key} className="mb-1">
                                    <strong>
                                        {t(KeysTitles[key]?.title || key)}:
                                    </strong> {value}
                                </Col>
                            ))}
                        </Row>
                    </CardBody>
                </Card>

                {/* Branches */}
                <Card className="h-100 h-100 shadow-sm mb-4">
                    <CardBody className="p-1">
                        <div className="d-flex align-items-center mb-2">
                            <span className="me-2 text-primary">
                                <RiArchiveLine size="24" />
                            </span>

                            <div className="fw-bold font-size-16">
                                {t('Branches')}
                            </div>
                        </div>

                        <Table striped bordered>
                            <thead>
                                <tr>
                                    <th>{t('Company Number')}</th>
                                    <th>{t('Name')}</th>
                                    <th>{t('Type')}</th>
                                    <th>{t('Line of Business')}</th>
                                    <th>{t('Status')}</th>
                                    <th>{t('Active')}</th>
                                    <th>{t('APE/Activity Code')}</th>
                                    <th>{t('Safe Number')}</th>
                                    <th>{t('Address')}</th>
                                </tr>
                            </thead>
                            <tbody>
                                {branches.map((branch) => (
                                    <tr key={branch.id}>
                                        <td>{branch.companyNumber}</td>
                                        <td>{branch.name}</td>
                                        <td>{branch.type}</td>
                                        <td>{branch.lineOfBusiness}</td>
                                        <td>{branch.status}</td>
                                        <td>{branch.isActive}</td>
                                        <td>{branch.activityApe}</td>
                                        <td>{branch.safeNumber}</td>
                                        <td>
                                            {branch.address?.street} {branch.address?.city} {branch.address?.postalCode} {branch.address?.telephone || ''}
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </Table>
                    </CardBody>
                </Card>

                {/* Directors / Auditors */}
                <Card className="h-100 h-100 shadow-sm mb-4">
                    <CardBody className="p-1">
                        <div className="d-flex align-items-center mb-2">
                            <span className="me-2 text-primary">
                                <RiUser3Line size="24" />
                            </span>

                            <div className="fw-bold font-size-16">
                                {t('Directors / Auditors')}
                            </div>
                        </div>

                         <Table striped bordered>
                            <thead>
                                <tr>
                                    <th>{t('Name')}</th>
                                    <th>{t('Gender')}</th>
                                    <th>{t('Director Type')}</th>
                                    <th>{t('Positions')}</th>
                                    <th>{t('Auditor Type')}</th>
                                    <th>{t('Corporation Type')}</th>
                                    <th>{t('PNR')}</th>
                                    <th>{t('SK')}</th>
                                </tr>
                            </thead>
                            <tbody>
                                {additionalAuditorInformation.map((auditor, i) => (
                                    <tr key={i}>
                                        <td>{auditor.name}</td>
                                        <td>{auditor.gender}</td>
                                        <td>{auditor.directorType}</td>
                                        <td>{auditor.positions.map(p => p.positionName).join(', ')}</td>
                                        <td>{auditor.auditorType}</td>
                                        <td>{auditor.type}</td>
                                        <td>{auditor.additionalData?.pnr}</td>
                                        <td>{auditor.additionalData?.sk}</td>
                                    </tr>
                                ))}
                            </tbody>
                        </Table>
                    </CardBody>
                </Card>

                {/* Company Composition / Structure */}
                <Card className="h-100 h-100 shadow-sm mb-4">
                    <CardBody className="p-1">
                        <div className="d-flex align-items-center mb-2">
                            <span className="me-2 text-primary">
                                <RiOrganizationChart size="24" />
                            </span>

                            <div className="fw-bold font-size-16">
                                {t('Company Composition')}
                            </div>
                        </div>
                        
                        <Row>
                            {['numberOfDirectors', 'numberOfCompanies'].map(key => (
                                <Col sm="6" key={key} className="mb-1">
                                    <strong>
                                        {t(KeysTitles[key]?.title || key)}:
                                    </strong>{' '}
                                    {misc[key]}
                                </Col>
                            ))}
                        </Row>
                    </CardBody>
                </Card>
            </Accordion.Body>
        </Accordion.Item>
    )
}

export default withNamespaces()(
    Establishment
)